/**
 * Fristberechnung für die Ferienbetreuung (#953).
 *
 * Anmeldeschluss und Stornofrist werden aus zwei unabhängigen Quellen
 * abgeleitet: der Anmeldeschluss steht pro Ferienzeit in Firestore
 * (`settings.registrationDeadline`), die Stornofrist ergibt sich aus dem
 * Ferienbeginn minus `CANCELLATION_DEADLINE_WEEKS`. Liegt der Anmeldeschluss
 * später als die Stornofrist, entsteht ein Fenster, in dem man sich anmelden,
 * aber nicht mehr abmelden kann.
 */

import { isAfterBerlinDay } from './berlin-day.utils';

/**
 * Stornofrist allein aus dem Ferienbeginn ("X Wochen vor Beginn").
 */
export function calculateCancellationDeadlineFromStart(
  vacationStart: Date,
  cancellationDeadlineWeeks: number
): Date {
  const deadline = new Date(vacationStart);
  deadline.setDate(deadline.getDate() - cancellationDeadlineWeeks * 7);
  return deadline;
}

/**
 * Effektive Stornofrist: das spätere der beiden Daten aus Stornofrist und
 * Anmeldeschluss. Solange die Anmeldung offen ist, muss die Abmeldung
 * möglich bleiben — sonst kann sich ein Elternteil anmelden und sitzt
 * sofort fest.
 */
export function resolveEffectiveCancellationDeadline(
  vacationStart: Date,
  registrationDeadline: Date,
  cancellationDeadlineWeeks: number
): Date {
  const fromStart = calculateCancellationDeadlineFromStart(
    vacationStart,
    cancellationDeadlineWeeks
  );
  return registrationDeadline > fromStart ? registrationDeadline : fromStart;
}

/**
 * Ist die Abmeldung zum Zeitpunkt `now` noch möglich?
 *
 * Die effektive Stornofrist gilt bis zum Ende ihres Kalendertages, nicht nur
 * bis zur exakten Mitternachtssekunde — sonst schließt die Abmeldung schon
 * am Stichtag selbst (#959, analog zu #958).
 */
export function canUnregisterAt(
  now: Date,
  vacationStart: Date,
  registrationDeadline: Date,
  cancellationDeadlineWeeks: number
): boolean {
  return !isAfterBerlinDay(
    now,
    resolveEffectiveCancellationDeadline(
      vacationStart,
      registrationDeadline,
      cancellationDeadlineWeeks
    )
  );
}

/**
 * Ist der Anmeldeschluss zum Zeitpunkt `now` bereits verstrichen?
 *
 * `registrationDeadline` markiert nur den Stichtag (Timestamp liegt auf
 * Mitternacht) — die Anmeldung muss bis zum Ende dieses Tages möglich
 * bleiben, sonst schließt sie schon am Stichtag selbst (#958).
 */
export function isRegistrationClosedAt(
  now: Date,
  registrationDeadline: Date
): boolean {
  return isAfterBerlinDay(now, registrationDeadline);
}

/**
 * Greift die reguläre Stornofrist oder wurde sie vom späteren
 * Anmeldeschluss überschrieben? Steuert nur den Dialogtext.
 */
export function isDeadlineExtendedByRegistration(
  vacationStart: Date,
  registrationDeadline: Date,
  cancellationDeadlineWeeks: number
): boolean {
  return (
    registrationDeadline >
    calculateCancellationDeadlineFromStart(
      vacationStart,
      cancellationDeadlineWeeks
    )
  );
}
